import React from 'react';
import { Box, Typography } from '@mui/material';
import PublicIcon from '@mui/icons-material/Public';
import LockIcon from '@mui/icons-material/Lock';
import CheckIcon from '@mui/icons-material/Check';
import { motion } from 'framer-motion';
import type { ContestWizardState } from '../types';
import { editorialHeadlineSx, midnight, selectionCardSx } from '../midnightStyles';

const OPTIONS = [
    {
        value: 'public_submission' as const,
        label: 'Public Submissions',
        message: 'Submissions can be public to be eligible. This enables voting options.',
        description:
            'Maximum exposure for your brand. Your campaign will be visible on the Explore marketplace. Any qualified creator can discover and apply to your brief.',
        Icon: PublicIcon,
        tags: ['Global Reach', 'High Volume'],
    },
    {
        value: 'private_submission' as const,
        label: 'Private Submissions',
        message:
            'Submissions MUST be private (unpublished) to be eligible. This disables public voting and only the host, team members and jurors can see the entries.',
        description:
            'Curated and discreet. Your campaign is hidden from the public feed. Only creators you personally invite via secure link can view and submit content.',
        Icon: LockIcon,
        tags: ['Curated Network', 'Stealth Mode'],
    },
];

interface Props {
    state: ContestWizardState;
    onChange: (patch: Partial<ContestWizardState>) => void;
}

export function SubmissionTypeStep({ state, onChange }: Props) {
    return (
        <Box>
            <Box sx={{ mb: 6 }}>
                <Typography sx={{ ...editorialHeadlineSx, fontSize: { xs: 32, md: 44 }, color: midnight.onSurface }}>
                    Define Your{' '}
                    <Box component="span" sx={{ color: midnight.primary }}>
                        Reach
                    </Box>
                </Typography>
                <Typography sx={{ color: midnight.onSurfaceVariant, fontWeight: 500, maxWidth: 480, mt: 1 }}>
                    Choose how creators discover and interact with your campaign brand values.
                </Typography>
            </Box>

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    gap: 4,
                }}>
                {OPTIONS.map((opt, i) => {
                    const selected = state.submission_state === opt.value;
                    return (
                        <motion.div
                            key={opt.value}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.08, duration: 0.35 }}>
                            <Box
                                component="button"
                                type="button"
                                onClick={() =>
                                    onChange({
                                        submission_state: opt.value,
                                        submission_state_message: opt.message,
                                        voting_required:
                                            opt.value === 'private_submission' ? false : state.voting_required,
                                    })
                                }
                                sx={{
                                    ...selectionCardSx(selected),
                                    width: '100%',
                                    border: 'none',
                                    font: 'inherit',
                                }}>
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: 24,
                                        right: 24,
                                        width: 24,
                                        height: 24,
                                        borderRadius: '50%',
                                        border: '2px solid',
                                        borderColor: selected ? midnight.primary : midnight.outline,
                                        bgcolor: selected ? midnight.primary : 'transparent',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}>
                                    {selected && <CheckIcon sx={{ fontSize: 16, color: '#392700' }} />}
                                </Box>
                                <Box
                                    sx={{
                                        width: 64,
                                        height: 64,
                                        mb: 3,
                                        borderRadius: '12px',
                                        bgcolor: midnight.surfaceHighest,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: midnight.primary,
                                    }}>
                                    <opt.Icon sx={{ fontSize: 36 }} />
                                </Box>
                                <Typography
                                    sx={{
                                        ...editorialHeadlineSx,
                                        fontSize: 22,
                                        color: midnight.onSurface,
                                        mb: 2,
                                    }}>
                                    {opt.label}
                                </Typography>
                                <Typography
                                    sx={{
                                        color: midnight.onSurfaceVariant,
                                        fontSize: 14,
                                        lineHeight: 1.7,
                                        mb: 4,
                                        flex: 1,
                                    }}>
                                    {opt.description}
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {opt.tags.map(tag => (
                                        <Box
                                            key={tag}
                                            sx={{
                                                px: 1.5,
                                                py: 0.5,
                                                borderRadius: '8px',
                                                bgcolor: midnight.surfaceHighest,
                                                fontSize: 10,
                                                fontWeight: 700,
                                                letterSpacing: '0.08em',
                                                textTransform: 'uppercase',
                                                color: midnight.onSurfaceVariant,
                                            }}>
                                            {tag}
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        </motion.div>
                    );
                })}
            </Box>
        </Box>
    );
}
